import { FC } from 'react';
import clsx from 'classnames';
import { chartColors, colors, ColorVariant } from '../../constants/colors';

import { CompareValues } from '../MultiFilter/MultiFilters.types';
import { getEntityName } from '../../utilities/helpers';
import { getOrdinalSuffix } from '../FiltersPanel/FiltersPanelAllFilters';
import { Tooltip } from 'antd';

type ChartReportFooterLabelsProps = {
  sourceData: { name: string }[];
  allFilters?: AllFilters;
  compareBy?: CompareValues;
  colorsPalette?: string[];
  isUtilizationChart?: boolean;
};

export const ChartReportFooterLabels: FC<ChartReportFooterLabelsProps> = ({
  compareBy,
  sourceData,
  allFilters,
  colorsPalette = chartColors,
  isUtilizationChart = false,
}) => {
  // For other charts
  const labelsFooterData = sourceData
    .filter((d) => d.name && d.name !== 'all')
    .map((item, i) => {
      let label = getEntityName({ value: item.name, allFilters, compareBy });
      if (compareBy == 'case_number') {
        label = `${item.name}${getOrdinalSuffix(parseInt(item.name))} Case`;
      }
      return (
        <div key={item.name} className="flex mx-12 mb-4">
          <div
            style={{
              backgroundColor: /rest/i.test(item.name) ? colors.neutralGray : colorsPalette[i],
            }}
            className="w-12 h-[1.6rem] rounded-[.4rem] mr-4"
          ></div>
          <span className="text-[1.2rem] leading-6 font-lato2 font-bold opacity-70 align-top">
            {label === 'rest' ? 'Rest' : label}
          </span>
        </div>
      );
    });

  // TODO: For Utilization
  const groupCount = new window.Set(sourceData.map((t) => t.name?.split('-')?.[1]).filter(Boolean))
    .size;
  const UtilizationLabelsFooterData = sourceData
    .filter((d) => d.name && d.name !== 'all')
    .splice(0, groupCount)
    .map((item, i) => {
      let label = getEntityName({ value: item.name?.split('-')?.[1], allFilters, compareBy });
      if (compareBy == 'case_number') {
        label = `${item.name}${getOrdinalSuffix(parseInt(item.name))} Case`;
      }

      if (label == '0') {
        return <></>;
      }
      return (
        <div key={item.name} className="flex mx-3 mb-4">
          <div
            style={{
              backgroundColor: /rest/i.test(item.name)
                ? colors.neutralGray
                : ColorVariant(
                    [...chartColors][i % groupCount],
                    1 - Math.floor(i / groupCount) / groupCount,
                  ),
            }}
            className="w-12 h-[1.6rem] rounded-[.4rem] mr-4"
          ></div>
          <span className="text-[1.2rem] leading-6 font-lato2 font-bold opacity-70 align-top">
            {label === '0' ? 'All' : label}
          </span>
        </div>
      );
    });

  return (
    <div
      data-testid="chart-footer"
      className={clsx('mt-4 w-full', {
        'grid grid-cols-1': labelsFooterData.length > 10,
        'flex flex-wrap justify-center items-center content-center': labelsFooterData.length <= 10,
      })}
    >
      {isUtilizationChart ? (
        <div className="w-full flex justify-center">
          <div className="flex justify-center">{UtilizationLabelsFooterData}</div>
        </div>
      ) : (
        labelsFooterData
      )}
    </div>
  );
};

// Gray Scale:
// const modalityPatterns = {
//   Robotic: 'bg-gray-500',
//   Lap: 'bg-[repeating-linear-gradient(135deg,#ffffff,#ffffff_2px,#6b7280_2px,#6b7280_6px)]',
//   Open: 'bg-[radial-gradient(circle,#FFFFFF_1px,transparent_1px)] [background-size:6px_6px] bg-gray-500',
//   Other: 'bg-[repeating-linear-gradient(45deg,#ffffff,#ffffff_2px,#6b7280_2px,#6b7280_6px)]',
//   // This should be like zigzag-vertical in patternomaly
//   'After Hours':
//     'bg-[repeating-linear-gradient(90deg,#ffffff,#ffffff_2px,#6b7280_1px,#6b7280_6px)]',
// };

const modalityPatterns = {
  Robotic: 'bg-[#1422bd]',
  Lap: 'bg-[repeating-linear-gradient(135deg,#ffffff,#ffffff_1px,#1422bd_2px,#1422bd_6px)]',
  Open: 'bg-[radial-gradient(circle,#FFFFFF_1px,transparent_1px)] [background-size:6px_6px] bg-[#1422bd]',
  Other: 'bg-[repeating-linear-gradient(45deg,#ffffff,#ffffff_1px,#1422bd_2px,#1422bd_6px)]',
  After_hours: 'bg-[repeating-linear-gradient(90deg,#ffffff,#ffffff_1px,#1422bd_1px,#1422bd_6px)]',
};

export const ModalityLegend: FC<any> = ({ filterState, multiFilterStore, isPreview }) => {
  const modalityState =
    filterState?.stackedBy?.length != 0
      ? filterState.stackedBy
      : ['Open', 'Lap', 'Robotic', 'Other']; // Default selection (all except After Hours)

  const normalizationType = filterState.normalization;

  const REPORT_NAME = 'utilization';

  return (
    <>
      {['After_hours', 'Other', 'Open', 'Lap', 'Robotic'].map((modality, i) => (
        <Tooltip
          overlayInnerStyle={{ borderRadius: '4px' }}
          key={i}
          title={
            modality == 'After_hours' && !!normalizationType
              ? "To select 'After hours', you need to set 'Normalize' to 'None'"
              : ''
          }
        >
          <div
            className={`flex mb-5 ${
              !isPreview && 'border-2 hover:bg-slate-200'
            } border-gray-200 p-1 ml-8 rounded-lg transition-all duration-200 ${
              modality == 'After_hours' && !!normalizationType
                ? 'cursor-not-allowed'
                : 'cursor-pointer'
            }  ${!modalityState.includes(modality) && 'opacity-40'}`}
            key={modality}
            onClick={() => {
              if (modality == 'After_hours' && !!normalizationType) return;

              let newModalityState = [];
              if (modalityState.includes(modality)) {
                newModalityState = modalityState.filter((m: string) => m !== modality);
              } else {
                newModalityState = [...modalityState, modality];
              }
              multiFilterStore.setFilters(REPORT_NAME, 'stackedBy', newModalityState);
            }}
          >
            <div
              className={`w-12 h-[1.6rem] rounded-[.4rem] mr-4 border border-gray-300 ${
                modalityPatterns[modality as keyof typeof modalityPatterns]
              } `}
            ></div>
            <span className="text-[1.2rem] leading-6 font-lato2 font-bold opacity-70 align-top">
              {modality.replace('_', ' ')}
            </span>
          </div>
        </Tooltip>
      ))}
    </>
  );
};
